// Adapters that map the mobx store's `allBreakdown` shape into the props the
// presentational advice-flow investments components expect. Keeping this glue
// here means the components stay pure and prop-driven while integration is a
// one-liner at the call site:
//
//   const accounts = mapAccounts(store.allBreakdown)
//   const categories = mapCategories(store.allBreakdownDetailed, activeAccount)

// Map engine AssetTypes IDs to their labels and display order. Type 5
// (Target-Date) has no entry here and so falls into "Misc" via
// `holdingCategoryName`'s fallback below: there's no dedicated icon for it,
// and the categories users need to reconcile are cash, bonds, stocks, and
// other (#21640) - not a fifth, separately-labeled bucket.
const ASSET_TYPES = {
  1: { name: 'Stocks', order: 3 },
  2: { name: 'Bonds', order: 2 },
  3: { name: 'Cash', order: 1 },
  4: { name: 'Misc', order: 4 },
}

const CATEGORY_ORDER = Object.fromEntries(
  Object.values(ASSET_TYPES).map(({ name, order }) => [name, order])
)

/** `store.allBreakdown` -> AdviceInvestmentTabs `accounts`. */
export const mapAccounts = (allBreakdown = []) =>
  allBreakdown.map(account => ({ id: account.name, name: account.name }))

// A category exposes only its assets after the store cleans the data, so a
// category's own current/recommended totals are the sum of its assets'.
const sumValues = (assets, pick) =>
  assets.reduce(
    (acc, asset) => {
      const value = asset[pick]
      if (value) {
        acc.percent += value.percent || 0
        acc.dollar += value.dollar || 0
        acc.present = true
      }
      return acc
    },
    { percent: 0, dollar: 0, present: false }
  )

const toValue = totals =>
  totals.present ? { percent: totals.percent, dollar: totals.dollar } : null

// Investment-level rows show the ticker in parentheses after the name, e.g.
// "Vanguard Total Stock (VTSAX)". Ticker is only present when reading from the
// store's `allBreakdownDetailed` view; it's omitted otherwise.
const mapAsset = asset => ({
  name: asset.ticker ? `${asset.name} (${asset.ticker})` : asset.name,
  current: asset.oldValue || null,
  recommended: asset.newValue || null,
})

// Asset-class label for a group of holdings. Holdings groups are named after the
// holding itself, so `type` — the key they share with the asset-class breakdown —
// is what names the class: prefer the account's own label for that type and fall
// back to the engine's asset-type names. Untyped groups, and type 5
// (Target-Date, not in ASSET_TYPES), roll up under "Misc", as they do in the
// older investment-mix charts.
const holdingCategoryName = (type, nameByType) =>
  nameByType.get(type) || ASSET_TYPES[type]?.name || 'Misc'

// Group every holding under its asset class (by `type`, not the holdings
// group's own name - see #21051 and PR 101019, which hit the same per-fund
// naming issue on the mobile cards). Shared by `mapCategories` and
// `mapHoldingCategories` so summary and details totals always agree with each
// other — sourcing summary totals from `assetClassBreakdown` instead produced
// different numbers than details for the same account, since that field is a
// look-through exposure breakdown rather than a holdings classification.
const groupHoldingsByAssetClass = account => {
  const nameByType = new Map(
    (account.assetClassBreakdown || [])
      .filter(category => category.type != null && category.name)
      .map(category => [category.type, category.name])
  )

  return (account.investmentHoldings || []).reduce((byCategory, group) => {
    const assets = group.assets || []
    if (assets.length === 0) {
      return byCategory
    }
    const name = holdingCategoryName(group.type, nameByType)
    byCategory.set(name, (byCategory.get(name) || []).concat(assets))
    return byCategory
  }, new Map())
}

const toCategoryTotals = (name, assets) => ({
  name,
  current: toValue(sumValues(assets, 'oldValue')),
  recommended: toValue(sumValues(assets, 'newValue')),
  investments: [],
})

// Whether an account has any holdings-level data at all. An unfunded account
// (no lineup yet) gets none - just a generic recommended asset-class mix -
// so callers fall back to `assetClassBreakdown` in that case only; there's no
// holdings-level view to disagree with it there (see `supportsDetails` in
// Investments.js).
const hasHoldingsData = account =>
  (account.investmentHoldings || []).some(group => (group.assets || []).length > 0)

/**
 * `store.allBreakdownDetailed` -> AdviceAssetAllocation `categories` for one
 * account: the asset-class summary (one row per class, no individual
 * holdings). Built from the same per-holding grouping as
 * `mapHoldingCategories` so toggling to Details never changes the totals.
 *
 * Falls back to `assetClassBreakdown` directly when the account has no
 * holdings-level data at all (e.g. an unfunded account only gets a generic
 * recommended asset-class mix, no specific funds) — Details isn't offered in
 * that case either (see `supportsDetails` in Investments.js), so there's
 * nothing for this fallback to disagree with.
 *
 * @param allBreakdownDetailed  the detailed view; the plain `allBreakdown` carries no `type`
 * @param accountName           which account (tab) to map
 */
export const mapCategories = (allBreakdownDetailed = [], accountName) => {
  const account =
    allBreakdownDetailed.find(item => item.name === accountName) || allBreakdownDetailed[0] || null
  if (!account) {
    return []
  }

  const categories = hasHoldingsData(account)
    ? [...groupHoldingsByAssetClass(account).entries()].map(([name, assets]) =>
        toCategoryTotals(name, assets)
      )
    : (account.assetClassBreakdown || []).map(category =>
        toCategoryTotals(category.name, category.assets || [])
      )

  // Drop asset classes with no data on either side so we don't render an empty
  // card/row for a category the account holds nothing in.
  return categories
    .filter(category => category.current || category.recommended)
    .sort((a, b) => (CATEGORY_ORDER[a.name] || 99) - (CATEGORY_ORDER[b.name] || 99))
}

/**
 * `store.allBreakdownDetailed` -> AdviceAssetAllocation `categories` for the
 * desktop investment-level (details) view: every holding in the account, grouped
 * under its asset class so each group leads with that class's icon and the view's
 * legend names the classes rather than the holdings.
 *
 * @param allBreakdownDetailed  the detailed view; the plain `allBreakdown` carries no `type`/`ticker`
 * @param accountName           which account (tab) to map
 */
export const mapHoldingCategories = (allBreakdownDetailed = [], accountName) => {
  const account =
    allBreakdownDetailed.find(item => item.name === accountName) || allBreakdownDetailed[0] || null
  if (!account) {
    return []
  }

  return [...groupHoldingsByAssetClass(account).entries()]
    .map(([name, assets]) => ({
      name,
      current: toValue(sumValues(assets, 'oldValue')),
      recommended: toValue(sumValues(assets, 'newValue')),
      investments: assets.map(mapAsset),
    }))
    .filter(category => category.current || category.recommended)
    .sort((a, b) => (CATEGORY_ORDER[a.name] || 99) - (CATEGORY_ORDER[b.name] || 99))
}

/**
 * Builds mobile cards: one per asset class, sourced from `investmentHoldings`
 * (grouped by `type`, consistent with Details) so a card's total always
 * agrees with its own expandable list. Basing a card's total on
 * `assetClassBreakdown` while its list came from `investmentHoldings` used to
 * let a holding's dollar amount count toward a card while the holding itself
 * was missing from every list (#21030, PR 101019) - and building the card
 * list from `assetClassBreakdown`'s categories alone dropped a holdings type
 * it doesn't return (e.g. Misc) entirely.
 *
 * Falls back to `assetClassBreakdown` directly when the account has no
 * holdings-level data at all, same as `mapCategories`.
 *
 * @param allBreakdownDetailed  the detailed view; the plain `allBreakdown` carries no `type`
 * @param accountName           which account to map
 */
export const mapAccountCategories = (allBreakdownDetailed = [], accountName) => {
  const account =
    allBreakdownDetailed.find(item => item.name === accountName) || allBreakdownDetailed[0] || null
  if (!account) {
    return []
  }

  const categories = hasHoldingsData(account)
    ? [...groupHoldingsByAssetClass(account).entries()].map(([name, assets]) => ({
        ...toCategoryTotals(name, assets),
        investments: assets.map(mapAsset),
      }))
    : (account.assetClassBreakdown || []).map(category =>
        toCategoryTotals(category.name, category.assets || [])
      )

  return categories
    .filter(category => category.current || category.recommended)
    .sort((a, b) => (CATEGORY_ORDER[a.name] || 99) - (CATEGORY_ORDER[b.name] || 99))
}

/**
 * Aggregate "Overall" categories across several accounts for the portfolio-wide
 * summary tab. Each account's holdings are grouped by asset class first (same
 * as `mapCategories`, falling back to `assetClassBreakdown` for an account
 * with no holdings-level data at all), then dollar amounts are summed per
 * asset class across the included accounts; percentages are recomputed from
 * those dollar totals (percentages are per-account, so they can't just be
 * added). Aggregating raw `assetClassBreakdown` categories directly used to
 * mean Overall could never show a Misc/Other row, since that field never
 * returns one. The result is summary-only (`investments: []`), so the Overall
 * view never offers an investment-level breakdown or expandable cards.
 *
 * @param allBreakdownDetailed  the store's allBreakdownDetailed array
 * @param accountNames          which accounts to include in the aggregate
 */
export const mapOverallCategories = (allBreakdownDetailed = [], accountNames = []) => {
  const include = new Set(accountNames)
  const byName = new Map()
  let totalCurrent = 0
  let totalRecommended = 0

  allBreakdownDetailed
    .filter(account => include.has(account.name))
    .forEach(account => {
      const accountCategories = hasHoldingsData(account)
        ? [...groupHoldingsByAssetClass(account).entries()]
        : (account.assetClassBreakdown || []).map(category => [
            category.name,
            category.assets || [],
          ])

      accountCategories.forEach(([name, assets]) => {
        const totals = byName.get(name) || {
          name,
          current: 0,
          recommended: 0,
          hasCurrent: false,
          hasRecommended: false,
        }
        assets.forEach(asset => {
          if (asset.oldValue) {
            totals.current += asset.oldValue.dollar || 0
            totals.hasCurrent = true
            totalCurrent += asset.oldValue.dollar || 0
          }
          if (asset.newValue) {
            totals.recommended += asset.newValue.dollar || 0
            totals.hasRecommended = true
            totalRecommended += asset.newValue.dollar || 0
          }
        })
        byName.set(name, totals)
      })
    })

  return (
    [...byName.values()]
      .map(totals => ({
        name: totals.name,
        current: totals.hasCurrent
          ? {
              dollar: totals.current,
              percent: totalCurrent ? (totals.current / totalCurrent) * 100 : 0,
            }
          : null,
        recommended: totals.hasRecommended
          ? {
              dollar: totals.recommended,
              percent: totalRecommended ? (totals.recommended / totalRecommended) * 100 : 0,
            }
          : null,
        investments: [],
      }))
      // Drop asset classes with no data on either side (empty across every account).
      .filter(category => category.current || category.recommended)
      .sort((a, b) => (CATEGORY_ORDER[a.name] || 99) - (CATEGORY_ORDER[b.name] || 99))
  )
}

// Return the risk value's position within the recommended range.
// Values below or above the range use index -1 and are identified by `text`.
const riskPosition = (value, min, max) => {
  if (value === null || value === undefined || min === null || max === null) {
    return { text: '', index: -1 }
  }
  if (value < min) {
    return { text: 'Lower than', index: -1 }
  }
  if (value > max) {
    return { text: 'Higher than', index: -1 }
  }
  const difference = Math.max(max - min, 0)
  let index = 2
  if (value < min + difference / 3) {
    index = 0
  } else if (value < min + (2 * difference) / 3) {
    index = 1
  }
  return { text: 'Within', index }
}

/**
 * Build the current and recommended risk positions for the advice risk rows.
 *
 * Step-by-step advice uses `riskLevel`, which covers the accounts included in
 * advice. Overall Strategy uses `riskLevelAll`, which covers all investment
 * accounts.
 *
 * Compare both values against the active case's recommended risk range so the
 * current and recommended positions use the same scale.
 *
 * @param baseCase    store.baseCase
 * @param activeCase  store.modifiedCase || store.recommendedCase
 * @param label       optional row label
 */
export const buildRisk = ({ baseCase, activeCase, label }) => {
  if (!baseCase || !activeCase) {
    return null
  }

  const min = activeCase.recomMinRiskLevel
  const max = activeCase.recomMaxRiskLevel

  return {
    label,
    current: riskPosition(baseCase.riskLevel, min, max),
    recommended: riskPosition(activeCase.riskLevel, min, max),
  }
}
